import { Component, inject } from '@angular/core';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatError, MatInput } from '@angular/material/input';
import { NgClass, NgOptimizedImage } from '@angular/common';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { SetPasswordService } from '../../../services/set-password.service';
import { SharedStorageService } from '../../../services/shared-storage.service';
import { ToastrService } from 'ngx-toastr';
import { Router } from '@angular/router';
import SHA256 from 'crypto-js/sha256';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { SingleActionDialogComponent } from '../single-action-dialog/single-action-dialog.component';
import { ChangePasswordRequest } from '../../../interfaces/set-password.model';
import { ModifyInfoService } from '../../../services/modify-info.service';
import { AuthService } from '../../../services/auth.service';
import { AlphaNumericOnlyDirective } from '../../../../directives/alpha-numeric-only.directive';

/**
 * 會員中心修改密碼dialog組件
 */
@Component({
  selector: 'app-modify-password-dialog',
  standalone: true,
  imports: [
    MatButton,
    MatError,
    MatIconButton,
    MatInput,
    NgOptimizedImage,
    ReactiveFormsModule,
    TranslatePipe,
    NgClass,
    AlphaNumericOnlyDirective,
  ],
  templateUrl: './modify-password-dialog.component.html',
  styleUrl: './modify-password-dialog.component.scss',
})
export class ModifyPasswordDialogComponent {
  translateService: TranslateService = inject(TranslateService);
  setPasswordService: SetPasswordService = inject(SetPasswordService);
  sharedStorageService: SharedStorageService = inject(SharedStorageService);
  toastr: ToastrService = inject(ToastrService);
  authService: AuthService = inject(AuthService);
  modifyInfoService: ModifyInfoService = inject(ModifyInfoService);
  router: Router = inject(Router);
  showFormError: boolean = false;
  dialog: MatDialog = inject(MatDialog);
  currentPasswordControl = new FormControl('', [Validators.required]); // 當前密碼
  showPasswordError: boolean = false; // 是否顯示當前密碼錯誤訊息
  passwordErrorMessage: string = ''; // 當前密碼錯誤訊息

  constructor(public dialogRef: MatDialogRef<ModifyPasswordDialogComponent>) {}

  setPasswordForm = new FormGroup({
    password: new FormControl('', [
      Validators.required,
      (control: FormControl) => {
        const result = this.passwordValidator(control);
        // 當 password 變更時，觸發 reEnterPassword 重新驗證
        setTimeout(() => {
          this.setPasswordForm?.get('reEnterPassword')?.updateValueAndValidity();
        });
        return result;
      },
    ]),
    reEnterPassword: new FormControl('', [
      Validators.required,
      (control: FormControl) => this.reEnterPasswordValidator(control),
    ]),
  });

  isCurrentPasswordVisible = false;
  isNewPasswordVisible = false;
  isReEnterPasswordVisible = false;

  toggleCurrentPasswordVisibility() {
    this.isCurrentPasswordVisible = !this.isCurrentPasswordVisible;
  }

  toggleNewPasswordVisibility() {
    this.isNewPasswordVisible = !this.isNewPasswordVisible;
  }

  toggleReEnterPasswordVisibility() {
    this.isReEnterPasswordVisible = !this.isReEnterPasswordVisible;
  }

  // 密碼驗證器
  passwordValidator(control: FormControl): ValidationErrors | null {
    const value = control.value || '';

    // 驗證8-20位英文數字組合
    const lengthValid = /^[A-Za-z0-9]{8,20}$/.test(value);

    // 驗證必須包含大小寫字母與數字
    const upperValid = /[A-Z]/.test(value); // 至少一個大寫字母
    const lowerValid = /[a-z]/.test(value); // 至少一個小寫字母
    const numberValid = /[0-9]/.test(value); // 至少一個數字

    const formatValid = upperValid && lowerValid && numberValid;

    if (!lengthValid || !formatValid) {
      return {
        ...(lengthValid
          ? null
          : {
              length: this.translateService.instant(
                'error.set-password-error.length'
              ),
            }),
        ...(formatValid
          ? null
          : {
              format: this.translateService.instant(
                'error.set-password-error.format'
              ),
            }),
      };
    }

    return null;
  }

  // 確認密碼驗證器
  reEnterPasswordValidator(control: FormControl): ValidationErrors | null {
    const value = control.value || '';

    const passwordValue = this.setPasswordForm?.get('password')?.value || '';
    if (value !== passwordValue) {
      return {
        notMatch: this.translateService.instant(
          'error.set-password-error.not-match'
        ),
      };
    }
    return null;
  }

  // 獲取確認密碼錯誤訊息
  getReEnterPasswordErrorMessage() {
    const control = this.setPasswordForm.get('reEnterPassword');
    if (!control || !control.errors || !this.showFormError) return '';

    const errors = control.errors;
    if (errors['required']) {
      return this.translateService.instant(
        'error.set-password-error.re-enter-required'
      );
    }
    if (errors['notMatch']) {
      return errors['notMatch'];
    }
    return '';
  }

  modifyPassword() {
    this.showPasswordError = true;
    
    if (this.currentPasswordControl.valid) {
      this.passwordErrorMessage = '';
      
      // 加密密碼
      const currentPassword = this.currentPasswordControl.value ?? '';
      const encryptedPassword = SHA256(currentPassword).toString();
      
      this.modifyInfoService
        .verifyCurrentPassword(encryptedPassword)
        .subscribe({
          next: (res) => {
            if (res.success && res.statusCode === 'S0000') {
              // 驗證成功，進行修改密碼後續流程
              console.log('密碼驗證成功');
              this.changePassword();
            } else {
              console.log("驗證失敗");
              // 驗證失敗，顯示錯誤訊息
              this.showPasswordError = true;
              
              // 檢查是否為今日已修改密碼的錯誤
              if (res.errorMessage === '密碼一天僅可修改一次，請於隔日再嘗試') {
                this.passwordErrorMessage = res.errorMessage;
              } else if (res.statusCode === 'EC1009' || 
                        res.errorMessage === '原密碼驗證錯誤，請重新輸入') {
                // 密碼錯誤，但不禁用按鈕，讓用戶可以重新輸入
                this.passwordErrorMessage = res.errorMessage ?? '原密碼驗證錯誤，請重新輸入';
              } else {
                // 其他錯誤
                this.passwordErrorMessage = res.errorMessage ?? '';
              }
              
              console.log('驗證失敗，顯示錯誤訊息:', this.passwordErrorMessage);
            }
          },
          error: (error) => {
            console.error('驗證密碼失敗:', error);
          }
        });
    } else {
      this.showPasswordError = true;
      this.passwordErrorMessage = this.translateService.instant('error.password-error.required');
      return;
    }
  }

  // 修改密碼
  changePassword() {
    this.showPasswordError = true;
    this.showFormError = true;
    if (this.setPasswordForm.valid) {
      const password = this.setPasswordForm.get('password')?.value ?? '';
      const confirmPassword =
        this.setPasswordForm.get('reEnterPassword')?.value ?? '';
      const encryptedPassword = SHA256(password).toString();
      const encryptedConfirmPassword = SHA256(confirmPassword).toString();

      const request :ChangePasswordRequest= {
        actType:'1',
        password: encryptedPassword,
        confirmPassword: encryptedConfirmPassword,
      };
      console.log("修改密碼,actType:",request.actType);
      this.setPasswordService.changePassword(request, '').subscribe({
        next: (res) => {
          console.log('API response:', res);
          if (res.statusCode === 'S0000') {
            // 成功：關閉 dialog 並回傳 'success'（不在這裡顯示 toastr）
            console.log('Success! Closing dialog with success');
            this.dialogRef.close('success');
          } else if (res.statusCode === 'EC1005') {
            // 失敗：顯示錯誤訊息並關閉 dialog，回傳 'error'
            this.dialogRef.close('error');
          } else if (res.errorMessage === '不可與最近三次的密碼重複') {
            // 密碼重複：顯示特殊提示 dialog（不關閉主 dialog，讓使用者重新輸入）
            const modifyPasswordDialog = this.dialog.open(SingleActionDialogComponent, {
              disableClose: true,
              data: {
                title: this.translateService.instant(
                  'modify-password-dialog.reset-password'
                ),
                message: this.translateService.instant(
                  'modify-password-dialog.reset-password-message'
                ),
                showCloseButton: false,
                buttonText: this.translateService.instant(
                  'modify-password-dialog.reset-password-button'
                ),
                action: () => modifyPasswordDialog.close(),
              },
            });
            // 不關閉 this.dialogRef，讓使用者可以重新輸入
          } else {
            // 其他未預期的錯誤：關閉 dialog 並回傳 'error'
            this.dialogRef.close('error');
          }
        },
        error: (error) => {
          // API 呼叫失敗：關閉 dialog 並回傳 'error'
          console.error('修改密碼 API 失敗:', error);
          this.dialogRef.close('error');
        }
      });
    }
  }

  // 提交設定密碼表單
  onSubmit() {
    this.showPasswordError = true;
    this.showFormError = true;
    
    // 先驗證當前密碼
    if (this.currentPasswordControl.invalid) {
      this.passwordErrorMessage = this.translateService.instant('error.password-error.required');
      return;
    }
    
    // 再驗證新密碼表單
    if (this.setPasswordForm.invalid) {
      return;
    }
    
    // 執行密碼驗證和修改
    this.modifyPassword();
  }
}
